type SmsVerificationStatsData = {
  status: number;
  result: {
    dailyCounts: Array<{
      agentId: string;
      dailyCounts: Array<{
        date: string;
        count: number;
      }>;
      email: string;
    }>;
  };
};

type ChartDataPoint = {
  date: string;
  totalCount: number;
};

type AgentTableRow = {
  email: string;
  agentId: string;
  dayCounts: Array<{ day: number; count: number; date: string }>;
  total: number;
};

/**
 * Parses a date string in YYYYMMDD format to a Date object
 */
function parseDate(dateStr: string): Date {
  const year = parseInt(dateStr.substring(0, 4), 10);
  const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
  const day = parseInt(dateStr.substring(6, 8), 10);
  return new Date(year, month, day);
}

/**
 * Formats a Date object to YYYYMMDD string
 */
function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Transforms SMS verification stats data into chart-ready format.
 * Sums up counts across all agents for each date.
 * Optionally filters by a set of agent emails.
 *
 * @param data - The SMS verification stats data
 * @param filterEmails - Optional set of email addresses to filter by (case-insensitive)
 * @returns Array of data points with date and total count across filtered agents
 */
export function transformSmsVerificationStats(
  data: SmsVerificationStatsData,
  filterEmails?: Set<string>,
): Array<ChartDataPoint> {
  if (!data?.result?.dailyCounts) {
    return [];
  }

  // Map to accumulate counts by date
  const dateCountMap = new Map<string, number>();

  // Iterate through all agents
  for (const agent of data.result.dailyCounts) {
    // If filter is provided, only include agents whose email is in the filter set
    if (filterEmails && !filterEmails.has(agent.email.toLowerCase())) {
      continue;
    }

    // Iterate through each agent's daily counts
    for (const dailyCount of agent.dailyCounts) {
      const { date, count } = dailyCount;
      // Sum up counts for each date across filtered agents
      const currentCount = dateCountMap.get(date) || 0;
      dateCountMap.set(date, currentCount + count);
    }
  }

  // Convert map to array and sort by date
  const chartData: Array<ChartDataPoint> = Array.from(dateCountMap.entries())
    .map(([date, totalCount]) => ({
      date,
      totalCount,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

  return chartData;
}

type CsAgent = {
  PersonID: number;
  Name: string;
  Email: string;
  SupervisorID: number;
  agentId?: string;
  subordinates?: Array<CsAgent>;
};

/**
 * Extracts all unique emails from the CS agents structure (including supervisors and subordinates)
 */
function extractAllAgentEmails(csAgents: Array<CsAgent>): Set<string> {
  const emails = new Set<string>();

  function traverse(agents: Array<CsAgent>) {
    for (const agent of agents) {
      if (agent.Email) {
        emails.add(agent.Email.toLowerCase());
      }
      if (agent.subordinates && agent.subordinates.length > 0) {
        traverse(agent.subordinates);
      }
    }
  }

  traverse(csAgents);
  return emails;
}

/**
 * Extracts email -> agentId mappings from the CS agents structure
 */
function extractEmailToAgentId(csAgents: Array<CsAgent>): Map<string, string> {
  const emailToAgentId = new Map<string, string>();

  function traverse(agents: Array<CsAgent>) {
    for (const agent of agents) {
      if (agent.Email && agent.agentId) {
        emailToAgentId.set(agent.Email.toLowerCase(), agent.agentId);
      }
      if (agent.subordinates && agent.subordinates.length > 0) {
        traverse(agent.subordinates);
      }
    }
  }

  traverse(csAgents);
  return emailToAgentId;
}

/**
 * Transforms SMS verification stats data into table-ready format.
 * Creates rows for each agent with their counts for the given date range.
 * Includes all agents from csAgents, even if they have no stats data.
 *
 * @param data - The SMS verification stats data
 * @param csAgents - Array of CS agents to include in the table
 * @param dateRange - Optional { startDate, endDate } in YYYYMMDD format
 * @returns Array of agent rows with email and day counts
 */
export function transformSmsVerificationStatsForTable(
  data: SmsVerificationStatsData,
  csAgents: Array<CsAgent>,
  dateRange?: { startDate: string; endDate: string },
): Array<AgentTableRow> {
  // Extract all unique emails from csAgents
  const allAgentEmails = extractAllAgentEmails(csAgents);
  // Extract email -> agentId mappings from csAgents (hard-coded)
  const csAgentsEmailToAgentId = extractEmailToAgentId(csAgents);

  let referenceEndDate: Date;
  let numDays: number;

  if (dateRange) {
    const start = parseDate(dateRange.startDate);
    referenceEndDate = parseDate(dateRange.endDate);
    numDays = Math.round((referenceEndDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  } else {
    // Find the most recent date from stats data (if available)
    let mostRecentDate: Date | null = null;
    if (data?.result?.dailyCounts) {
      for (const agent of data.result.dailyCounts) {
        for (const dailyCount of agent.dailyCounts) {
          const date = parseDate(dailyCount.date);
          if (!mostRecentDate || date > mostRecentDate) {
            mostRecentDate = date;
          }
        }
      }
    }
    referenceEndDate = mostRecentDate || new Date();
    numDays = 21;
  }

  const dayColumns: Array<{ day: number; date: string }> = [];
  for (let i = numDays; i >= 1; i--) {
    const date = new Date(referenceEndDate);
    date.setDate(date.getDate() - (i - 1));
    dayColumns.push({
      day: i,
      date: formatDate(date),
    });
  }

  // Create a map of email -> stats data for quick lookup
  const statsByEmail = new Map<string, Map<string, number>>();
  // Create a map of email -> agentId from stats data
  const statsEmailToAgentId = new Map<string, string>();
  // Create a map of agentId -> stats data for agents without emails
  const statsByAgentId = new Map<string, Map<string, number>>();
  if (data?.result?.dailyCounts) {
    for (const agent of data.result.dailyCounts) {
      const dateCountMap = new Map<string, number>();
      for (const dailyCount of agent.dailyCounts) {
        dateCountMap.set(dailyCount.date, dailyCount.count);
      }

      if (agent.email) {
        const email = agent.email.toLowerCase();
        statsByEmail.set(email, dateCountMap);
        statsEmailToAgentId.set(email, agent.agentId);
      } else if (agent.agentId) {
        // Agent without email - store by agentId
        statsByAgentId.set(agent.agentId, dateCountMap);
      }
    }
  }

  // Create rows for all agents from csAgents
  const agentRows: Array<AgentTableRow> = [];
  const processedEmails = new Set<string>();
  const processedAgentIds = new Set<string>();

  // First, process all agents from csAgents
  for (const email of Array.from(allAgentEmails).sort()) {
    // Get stats data for this agent if available
    const agentDateCountMap = statsByEmail.get(email) || new Map<string, number>();
    // Get agentId: prefer from stats data, fallback to csAgents hard-coded mapping
    const agentId = statsEmailToAgentId.get(email) || csAgentsEmailToAgentId.get(email) || '';

    // Build day counts array for this agent
    const dayCounts = dayColumns.map(({ day, date }) => ({
      day,
      date,
      count: agentDateCountMap.get(date) || 0,
    }));

    const total = dayCounts.reduce((sum, dayCount) => sum + dayCount.count, 0);

    agentRows.push({
      email,
      agentId,
      dayCounts,
      total,
    });

    // Track processed emails and agentIds
    processedEmails.add(email);
    if (agentId) {
      processedAgentIds.add(agentId);
    }
  }

  // Then, process all agents from stats data that aren't in csAgents (including those without emails)
  if (data?.result?.dailyCounts) {
    for (const agent of data.result.dailyCounts) {
      const agentId = agent.agentId;
      const email = agent.email ? agent.email.toLowerCase() : null;

      // Skip if we already processed this agent (via email in csAgents)
      if (email && processedEmails.has(email)) {
        continue;
      }

      // Skip if we already processed this agentId
      if (agentId && processedAgentIds.has(agentId)) {
        continue;
      }

      // Get stats data for this agent
      const agentDateCountMap = email
        ? statsByEmail.get(email) || new Map<string, number>()
        : agentId
        ? statsByAgentId.get(agentId) || new Map<string, number>()
        : new Map<string, number>();

      // Build day counts array for this agent
      const dayCounts = dayColumns.map(({ day, date }) => ({
        day,
        date,
        count: agentDateCountMap.get(date) || 0,
      }));

      const total = dayCounts.reduce((sum, dayCount) => sum + dayCount.count, 0);

      // Include all agents from stats data (even if total is 0, as they might have data in other periods)
      agentRows.push({
        email: agent.email || '', // Empty string if no email
        agentId: agentId || '',
        dayCounts,
        total,
      });

      // Track processed emails and agentIds
      if (email) {
        processedEmails.add(email);
      }
      if (agentId) {
        processedAgentIds.add(agentId);
      }
    }
  }

  // Sort by total in descending order (highest first)
  agentRows.sort((a, b) => b.total - a.total);

  return agentRows;
}
